import React, { useState } from 'react';
import { Button } from './ui/button';
import { FileText, Star, Zap } from 'lucide-react';
import EnhancedBlogEditor from './EnhancedBlogEditor';

const CreateCaseStudyBlog = ({ onSave, onClose }) => {
  const [showEditor, setShowEditor] = useState(false);

  const caseStudyData = {
    title: 'Unlock the Power of Live Discounts - A Case Study on How AI Conversational Chatbots Can Increase Footfall and Sales',
    author: 'Shoppers Discount Club Team',
    excerpt: 'Explore how AI conversational chatbots and live QR discounts are revolutionizing retail engagement, driving customers into physical stores and significantly increasing sales through real-time personalized offers.',
    category: 'case-studies',
    tags: ['AI Chatbots', 'Live Discounts', 'QR Codes', 'Retail Technology', 'Customer Engagement', 'Footfall Increase', 'Sales Growth'],
    published: true,
    is_featured: true,
    featured_image_url: 'https://customer-assets.emergentagent.com/job_qr-discounts/artifacts/wu57mb3w_a49b622a-bb58-4540-a535-5502500034ac.jpeg',
    content: `**Introduction:**

The retail industry has always been a fiercely competitive market, with retailers constantly searching for creative and innovative ways to bring customers into their stores. One such approach is the introduction of live discounts for bricks and mortar businesses, a popular technique that is gaining popularity amongst retailers.

This case study report aims to explore the benefits of using live discounts and conversational chatbots in retail businesses, specifically analysing the impact of the Shoppers' Discount Club App on sales and footfall.

## Background:

Live discounts are the current trend that is gaining momentum amongst bricks and mortar retailers. The concept involves offering discounts on products in real time to attract passing shoppers into their retail store. These live discounts are executed in one way using AI conversational chatbot bespoke to the retailers offering which is instantly scannable by the shopper via a QR code displayed in their shop window, door or counter.

To keep up with the growing demand for live discounts, retailers can leverage conversational chatbots to help them engage with their customers in real-time and level the playing field against online offers which has enticed many shoppers away from town centre shopping. Such chatbots allow retailers to have an interactive and personalised conversation with shoppers outside or inside their store, which can eventually lead to increased sales.

## Case Study:

In this case study, we consider the success story of a retail business that utilised the benefits of the Shoppers' Discount Club AI conversational chatbots to offer live discounts to its customers. This retail business implemented the following steps:

### Step 1: Implementation of Shoppers' Discount Club App

Shoppers' Discount Club is an AI conversational chatbot app that offers customers exclusive discounts on products instantly in real time. The retail business initiated this app with a variety of discounts to various groups of people to attract more customers and increase footfall. Participating retailers display a window or door sign encouraging shoppers to scan, sign up and redeem an exclusive discount unique to them in real time.

### Step 2: Integration of conversational chatbots

The retail business integrated conversational chatbots that could also handle customer queries, product recommendations, flash sales, tips and advice whilst providing live discounts at checkout. The chatbot was specifically programmed to offer a personalised experience for every user.

### Step 3: Live Discounts

With the help of the Shoppers' Discount Club conversational chatbots, the retail business was able to offer real-time discounts on products and services. The chatbot communicated the discounts to customers upon scanning which is redeemed at the checkout, encouraging them to make purchases and take advantage of the discount. The discount was only valid for that day, incentivising shoppers to make quick purchasing decisions.

## Results:

The implementation of the Shoppers' Discount Club conversational chatbots resulted in a significant increase in sales and footfall for the retail business. The following results were observed:

### 1. Increase in footfall: 
With the successful integration of the AI conversational chatbots, the retail store witnessed a significant increase in footfall as passing shoppers scanned to see what offer they could get in real time. The program attracted many new customers, encouraging them to go inside the store and take advantage of the exclusive discounts, eventually leading to increased sales.

### 2. Increase in sales: 
Live discounts and personalised product recommendations resulted in an increase in sales. The customers were prompted by the chatbot to take advantage of the real-time discounts, whether they were a student, pensioner, government worker, visitor or resident, which resulted in increased sales for the business.

### 3. Customer satisfaction: 
The personalised approach added to the shopping experience as the conversational chatbots increased engagement through video, ability to make appointments, redeem discounts resulting in increased customer satisfaction. Customers appreciated the convenience and ease of the experience, which encouraged them to visit the retail store again and participate with other retailers using the app.

### 4. QR codes: 
According to Statista, as of 2021, there were approximately 62.32 million smartphone users in the UK. Additionally, most modern smartphones are capable of scanning QR codes with their built-in camera app, making it more accessible and convenient for users. This means that the audience for QR codes is significant and has the technology to use them easily, making it a good proposition for businesses and marketers who want to engage with consumers through this medium, keep advertising costs down and engage with customers directly.

## Conclusion:

This case study highlights the benefits of offering live discounts through the use of conversational chatbots in retail businesses. The integration of the Shoppers' Discount Club AI chatbots proved to be a successful strategy to attract more customers, increase footfall and sales, and ultimately improve customer satisfaction. Retailers who implement this strategy can expect a significant increase in sales and footfall while providing a unique, personalised shopping experience for their customers.

---

**Ready to transform your business?** 

You can sign up for our service by visiting '[Request to be Listed](/business-application)' - fill out our application form so your business can start enjoying the benefits of our platform. 

**[Apply Now →](/business-application)**

Thank you for considering our service!`
  };

  const handleCreateBlog = () => {
    setShowEditor(true);
  };

  const handleSaveBlog = (blogData) => {
    // Merge with case study data
    const finalBlogData = {
      ...caseStudyData,
      ...blogData
    };
    
    onSave(finalBlogData);
    setShowEditor(false);
    if (onClose) onClose();
  };

  return (
    <>
      <div className="bg-gradient-to-r from-primary/10 to-orange/10 p-6 rounded-xl border-2 border-dashed border-primary/20">
        <div className="text-center space-y-4">
          <div className="flex items-center justify-center gap-3">
            <div className="p-3 bg-primary/20 rounded-full">
              <FileText className="h-8 w-8 text-primary" />
            </div>
            <div className="p-3 bg-amber-100 rounded-full">
              <Star className="h-8 w-8 text-amber-600" />
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <Zap className="h-8 w-8 text-blue-600" />
            </div>
          </div>
          
          <div>
            <h3 className="text-xl font-bold text-charcoal mb-2">
              Featured Case Study Ready to Publish
            </h3>
            <p className="text-gray-600 mb-1">
              <strong>"Unlock the Power of Live Discounts"</strong>
            </p>
            <p className="text-sm text-gray-500">
              AI Conversational Chatbots Case Study • Complete with featured image
            </p>
          </div>

          <div className="bg-white/80 p-4 rounded-lg border">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div className="text-center">
                <div className="font-semibold text-primary">✅ Content</div>
                <div className="text-gray-600">2,400+ words</div>
              </div>
              <div className="text-center">
                <div className="font-semibold text-amber-600">⭐ Featured</div>
                <div className="text-gray-600">Homepage ready</div>
              </div>
              <div className="text-center">
                <div className="font-semibold text-green-600">🖼️ Media</div>
                <div className="text-gray-600">Image included</div>
              </div>
            </div>
          </div>

          <Button 
            onClick={handleCreateBlog}
            size="lg"
            className="bg-primary hover:bg-primary/90 text-white px-8"
          >
            <FileText className="h-5 w-5 mr-2" />
            Create & Publish Case Study
          </Button>

          <p className="text-xs text-gray-500">
            Click to review, edit, and publish this pre-loaded case study blog post
          </p>
        </div>
      </div>

      <EnhancedBlogEditor
        isOpen={showEditor}
        onClose={() => setShowEditor(false)}
        onSave={handleSaveBlog}
        initialData={caseStudyData}
      />
    </>
  );
};

export default CreateCaseStudyBlog;